export type AlertType = "info" | "warning" | "success" | "error" | "tilt";

const alertFrequencies: Record<AlertType, number[]> = {
  info: [440, 550],
  warning: [600, 500, 600],
  success: [523, 659, 784],
  error: [400, 300, 250],
  tilt: [800, 600, 800, 600],
};

const FINTHEON_SETTINGS_STORAGE_KEY = "fintheon:settings";

export function playAlertSound(type: AlertType, enabled: boolean = true): void {
  if (!enabled) return;

  try {
    const audioContext = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
    const frequencies = alertFrequencies[type];
    let startTime = audioContext.currentTime;

    frequencies.forEach((freq, index) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = freq;
      oscillator.type = "sine";

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);

      oscillator.start(startTime);
      oscillator.stop(startTime + 0.2);

      startTime += 0.25;
    });
  } catch (error) {
    console.error("Failed to play alert sound:", error);
  }
}

export function playVoiceReadySuccessSound(
  enabled: boolean = readStoredSoundEnabled(),
): void {
  playVoiceToneSequence({
    enabled,
    errorLabel: "voice ready sound",
    steps: [
      { frequency: 659.25, offset: 0, duration: 0.12 },
      { frequency: 880, offset: 0.1, duration: 0.14 },
    ],
  });
}

export function playVoiceEndSuccessSound(
  enabled: boolean = readStoredSoundEnabled(),
): void {
  playVoiceToneSequence({
    enabled,
    errorLabel: "voice end sound",
    peakGain: 0.034,
    steps: [
      { frequency: 783.99, offset: 0, duration: 0.1 },
      { frequency: 523.25, offset: 0.09, duration: 0.14 },
    ],
  });
}

export function playVoiceSidebarHandoffSound(
  enabled: boolean = readStoredSoundEnabled(),
): void {
  playVoiceToneSequence({
    enabled,
    errorLabel: "voice sidebar handoff sound",
    peakGain: 0.038,
    steps: [
      { frequency: 523.25, offset: 0, duration: 0.08 },
      { frequency: 659.25, offset: 0.075, duration: 0.08 },
      { frequency: 987.77, offset: 0.15, duration: 0.12 },
    ],
  });
}

/**
 * Play iOS-style ping sound for news alerts
 */
export function playIOSPing(enabled: boolean = true): void {
  if (!enabled) return;

  try {
    const audioContext = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
    const now = audioContext.currentTime;

    // iOS ping is a short, high-pitched tone (around 800Hz) with quick decay
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = "sine";
    oscillator.frequency.value = 800;

    // Quick attack and decay (like iOS notification sound)
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start(now);
    oscillator.stop(now + 0.15);
  } catch (error) {
    console.error("Failed to play iOS ping:", error);
  }
}

interface VoiceToneStep {
  frequency: number;
  offset: number;
  duration: number;
}

interface VoiceToneSequenceInput {
  enabled: boolean;
  errorLabel: string;
  steps: VoiceToneStep[];
  peakGain?: number;
}

function playVoiceToneSequence({
  enabled,
  errorLabel,
  steps,
  peakGain = 0.042,
}: VoiceToneSequenceInput): void {
  if (!enabled || steps.length === 0) return;
  if (typeof window === "undefined") return;

  try {
    const AudioContextCtor =
      window.AudioContext ||
      (
        window as typeof window & {
          webkitAudioContext?: typeof AudioContext;
        }
      ).webkitAudioContext;
    if (!AudioContextCtor) return;

    const audioContext = new AudioContextCtor();
    const now = audioContext.currentTime;
    const lastStep = steps[steps.length - 1];
    const endAt = now + lastStep.offset + lastStep.duration;
    const masterGain = audioContext.createGain();

    masterGain.gain.setValueAtTime(0.0001, now);
    masterGain.gain.exponentialRampToValueAtTime(peakGain, now + 0.012);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, endAt + 0.02);
    masterGain.connect(audioContext.destination);

    steps.forEach(({ frequency, offset, duration }) => {
      const oscillator = audioContext.createOscillator();
      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, now + offset);
      oscillator.connect(masterGain);
      oscillator.start(now + offset);
      oscillator.stop(now + offset + duration);
    });

    void audioContext.resume?.();
    window.setTimeout(
      () => {
        void audioContext.close().catch(() => {});
      },
      Math.ceil((endAt - now) * 1000) + 120,
    );
  } catch (error) {
    console.error(`Failed to play ${errorLabel}:`, error);
  }
}

function readStoredSoundEnabled(): boolean {
  if (typeof localStorage === "undefined") return true;

  try {
    const stored = localStorage.getItem(FINTHEON_SETTINGS_STORAGE_KEY);
    if (!stored) return true;
    return JSON.parse(stored)?.alertConfig?.soundEnabled !== false;
  } catch {
    return true;
  }
}
